import { useQuery } from '@tanstack/react-query';
import {
  EnhancedLightboxItem,
  FeedQueryResult,
} from '../../net/softbox-types.ts';

import './FeedLayout.scss';
import { FeedBuilder } from './FeedBuilder.tsx';
import { FeedBuilderInfinite } from './FeedBuilderInfinite.tsx';
import ActivityIndicator from '../../components/ActivityIndicator/ActivityIndicator.tsx';
import { MomentBaseProps } from '../../routes/types.ts';
import { LayoutConfigurationPage } from '../LayoutConfiguration/layout-configuration-factory.ts';
import { useSearchParams } from 'react-router';
import { AdhesionAd } from '../../components/AdhesionAd/AdhesionAd.tsx';
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { CommonPageDataContext } from '@pages/Root/ContextWrapper/WithInterest/CommonPageDataContext.ts';
import { useTranslation } from 'react-i18next';
import { useAds } from '@moment/context/AdsContext.tsx';
import { AdCounterContext } from '@moment/context/AdCounterContext.tsx';
import { DTXAdhesionAd } from '@moment/components/DTXAd/DTXAdhesionAd.tsx';
import { useAnalytics } from '@moment/analytics/AnalyticsProvider.tsx';
import { getCurrentNumberOfInteractions } from '@moment/local-personalization/scorer.ts';

export interface FeedLayoutProps
  extends MomentBaseProps<Record<string, string>, FeedQueryResult> {
  schedule: string;
  configuration: LayoutConfigurationPage;
}

const AdhesionRenderer = () => {
  const [searchParams] = useSearchParams();
  const { adSource } = useAds();

  const showAdhesion = searchParams.get('medium') === 'fsd';
  if (!showAdhesion) {
    return null;
  }

  const adhesionGamAd = <AdhesionAd adUnitId={`adh1`} />;
  const hasDtx = adSource.includes('dtx');
  const hasGam = adSource.includes('gam');

  if (hasDtx) {
    return (
      <DTXAdhesionAd
        adUnitId={'adhesion'}
        fallbackAd={hasGam ? adhesionGamAd : null}
      />
    );
  }
  if (hasGam) {
    return adhesionGamAd;
  }
  return null;
};

export const FeedLayout = (props: FeedLayoutProps) => {
  const [searchParams] = useSearchParams();
  const context = useContext(CommonPageDataContext);
  const { i18n } = useTranslation();
  const lang = i18n.language;

  // The feed's ad index decides the placement ('top' only for index 1), so it must restart for
  // every feed. The root AdsLayout's counter lives for the whole session; this one is per FeedLayout.
  const adCounter = useRef(0);
  const getAndIncrement = useCallback(() => {
    adCounter.current += 1;
    return adCounter.current;
  }, []);
  const adCounterContextValue = useMemo(
    () => ({ getAndIncrement }),
    [getAndIncrement]
  );

  //XXX NOTE - buildMomentRoute is mutating this element's props to add queryFn XXX
  const feedQueryConstruct = {
    queryKey: [props.configuration.path, lang],
    queryFn: props.queryFn({ lang }),
  };
  const analytics = useAnalytics();

  const { data, isLoading } = useQuery<FeedQueryResult>(feedQueryConstruct);

  const newsItems = useMemo(() => {
    return data?.news?.items
      ? data.news.items.map((it) => {
          return new EnhancedLightboxItem(it);
        })
      : [];
  }, [data]);

  useEffect(() => {
    if (newsItems?.length > 0) {
      analytics.emitPageViewEvent({
        numInteractions: getCurrentNumberOfInteractions(),
        lbtype: '',
        partnerId: context.partner || '',
        contentId: newsItems[0].uid,
        source: window.location.href,
        language: lang,
        title: document.querySelector('title')?.text || '',
        contentOwner: newsItems[0].owner,
        primaryInterest: newsItems[0].interests[0],
        searchParams,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newsItems]);

  const feedContent = useMemo(() => {
    if (!data) {
      return <></>;
    }
    return (
    <>
      <FeedBuilder
        configuration={props.configuration}
        newsItems={newsItems}
        interests={data.interests}
        partner={context.partner}
      />
      <FeedBuilderInfinite
        schedule={props.schedule}
        interests={data.interests}
        partner={context.partner}
        numberOfPages={context.numberOfPages || 4}
      />
    </>
  )}, [props.configuration, newsItems, data?.interests, context.partner, props.schedule, context.numberOfPages]);

  return (
    <div>
      <main>
        {isLoading && <ActivityIndicator />}
        {data && (
          <div className="moment-container">
            <div className="feed-container">
              <AdCounterContext.Provider value={adCounterContextValue}>
                {feedContent}
                <AdhesionRenderer />
              </AdCounterContext.Provider>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};
